import Photo from '@components/photo'
import SimpleLink from '@components/simple-link'
import MuxVideo from '@components/video/mux-video'
import { SanityLinkBlock } from '@data/sanity/queries/types/blocks'
import { SanityVideoAspectRatioValue } from '@data/sanity/queries/types/video'
import { textColorClasses } from '@lib/color'
import { getFormattedDate } from '@lib/format'
import { getImageRatio } from '@lib/image'
import { LanguageContext } from '@lib/language-context'
import { getFormattedQueryString, getPageUrl, PageType } from '@lib/routes'
import { LayoutContext } from '@lib/site-context'
import { StringsContext } from '@lib/strings-context'
import cx from 'classnames'
import { useContext, useMemo } from 'react'

type LinkBlockProps = Pick<
  SanityLinkBlock,
  | 'link'
  | 'collectionLinkContext'
  | 'parentCollection'
  | 'queryString'
  | 'url'
  | 'text'
  | 'textColor'
  | 'backgroundType'
  | 'mobilePhoto'
  | 'photo'
  | 'muxVideo'
> & {
  photoSizes?: string
  className?: string
}

const LinkBlock = ({
  link,
  collectionLinkContext,
  parentCollection,
  queryString,
  url,
  text,
  textColor,
  backgroundType,
  mobilePhoto,
  photo,
  photoSizes,
  muxVideo,
  className,
}: LinkBlockProps) => {
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)
  const { headerHeight } = useContext(LayoutContext)

  const resolvedUrl = useMemo(() => {
    if (url) {
      return url
    }

    if (link) {
      let pageType: PageType = link._type
      const slug = link.slug?.current ?? ''
      let slugs: string[] = [slug]

      if (link._type === 'collection' && collectionLinkContext) {
        if (collectionLinkContext === 'shopAll') {
          pageType = 'shopPageChild'
        }

        if (collectionLinkContext === 'sale') {
          pageType = 'shopSalePageChild'
        }

        if (
          collectionLinkContext === 'parent' &&
          parentCollection?.slug?.current
        ) {
          pageType = 'collectionChild'
          slugs = [parentCollection.slug.current, slug]
        }
      }

      const pageUrl = getPageUrl(locale, pageType, slugs)
      const formattedQueryString = getFormattedQueryString(queryString)

      return `${pageUrl}${formattedQueryString}`
    }

    return ''
  }, [collectionLinkContext, link, locale, parentCollection, queryString, url])

  const activePhoto = photo
  const activeMobilePhoto = mobilePhoto?.asset ? mobilePhoto : photo

  const content = (
    <>
      <div className="absolute inset-0">
        <span
          className={cx(
            'block sticky left-0 z-10 p-3',
            'uppercase text-xs leading-tight',
            textColorClasses[textColor],
          )}
          style={{
            top: headerHeight,
          }}
        >
          {!!link?.date && (
            <>
              {getFormattedDate(link.date, locale)}
              <br />
            </>
          )}
          {text}{' '}
          <span className="hidden group-hover:inline">
            —{' '}
            {link?._type === 'blogPost'
              ? strings.blogPostReadMore
              : strings.buttonShopNow}
          </span>
        </span>
      </div>

      {backgroundType === 'photo' && (
        <>
          {!!activePhoto && (
            <Photo
              image={activePhoto}
              showCaption={false}
              sizes={photoSizes}
              className="hidden sm:block relative h-full sm:p-0"
              style={{
                paddingTop: `${
                  100 / getImageRatio(activePhoto.customRatio, 16 / 9)
                }%`,
              }}
              imageClassName="w-full h-full object-cover absolute inset-0 static"
            />
          )}
          {!!activeMobilePhoto && (
            <Photo
              image={activeMobilePhoto}
              showCaption={false}
              sizes={photoSizes}
              className="sm:hidden relative h-full sm:p-0"
              style={{
                paddingTop: `${
                  100 / getImageRatio(activeMobilePhoto.customRatio, 5 / 7)
                }%`,
              }}
              imageClassName="w-full h-full object-cover absolute inset-0 static"
            />
          )}
        </>
      )}

      {backgroundType === 'mux-video' && !!muxVideo && (
        <MuxVideo
          video={muxVideo}
          showControls={false}
          autoplay
          muted
          loop
          customAspectRatio={{
            custom: true,
            mobile: '1:1',
            desktop: muxVideo.asset.data
              .aspect_ratio as SanityVideoAspectRatioValue,
          }}
        />
      )}
    </>
  )

  const wrapperClassName = cx('block h-full relative group', className)

  if (url) {
    return (
      <a
        href={resolvedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={wrapperClassName}
      >
        {content}
      </a>
    )
  }

  return (
    <SimpleLink href={resolvedUrl} className={wrapperClassName}>
      {content}
    </SimpleLink>
  )
}

export default LinkBlock
